"use client";

import { Fragment, useState } from "react";
import { useTranslations } from "next-intl";
import { useLocale } from "next-intl";
import Image from "next/image";
import dynamic from "next/dynamic";
import { Link } from "@/i18n/navigation";
import { artistLinkRel, eventLinkRel } from "@/lib/seo-enrichment";
import { MapPin, ExternalLink } from "lucide-react";
import { Event, Attraction } from "@/lib/types";
import { CITIES, getCitySlugForVenueCity } from "@/lib/constants";
import {
  EventGroup,
  formatTime,
  formatPriceCompact,
  getTransformedImageUrl,
  hasOnlyFallbackImages,
  isOwnImage,
  isDisplayableGenre,
} from "@/lib/utils";
import { getListCardPrimaryAttraction, isPlayableAct } from "@/lib/event-feed";
import type { InitialEventImage } from "@/lib/initial-event-image";
import { useSpotifyPlayer } from "../spotify/spotify-player-context";
import { useAuth } from "../auth/auth-context";
import { SubscribeButton } from "../subscribe-button";
import { ArtistExternalLinks } from "../artist-external-links";
import { ReportButton } from "../report/report-button";
import { EventGenreChip as GenreChip } from "./event-genre-chip";
import { PlayIcon } from "@/components/spotify/play-icon";
import { DeferredListImage } from "./deferred-list-image";

const EventShowingsModal = dynamic(() => import("./event-showings-modal").then((module) => module.EventShowingsModal), { ssr: false });
const EventAdminModal = dynamic(() => import("../admin/event-admin-modal").then((module) => module.EventAdminModal), { ssr: false });

interface EventCardListProps {
  event: Event;
  eventGroup?: EventGroup;
  blockedSpotifyIds: string[];
  citySlug?: string;
  showCity?: boolean;
  prioritizeImage?: boolean;
  initialImage?: InitialEventImage;
}

/* Desktop columns: who (tile) · lineup · logistics. The right column is a
   short stack — venue, then time and tickets — whose first line sits on the
   same baseline as the first act's name in the lineup column, so the two
   columns read as one row without a shared header band. The two paddings
   below are what keep that alignment: lineup wrapper 8px + artist row 8px +
   half a 28px name row = 30px; right column 20px + half a 20px line = 30px.
   Mobile gets the same 8px so the pack of rows breathes under the tile. */
const LINEUP_TOP = "py-2";
const RIGHT_COL_TOP = "lg:pt-5";

const LINEUP_PREVIEW = 3;

/* Right column inset. Everything in the column hangs off one right edge,
   18px in from the card: the text block, and the bell *icon* (its 40px hit
   area is pulled 12px past the padding so the 16px glyph lands on the same
   line). Same on compact and full cards. */
const RIGHT_COL_X = "lg:px-[18px]";
const BELL_SLOT = "relative z-10 pointer-events-auto -mr-3 flex shrink-0 items-center gap-1.5";
/* With the play disc in the corner the bell sits a gap to its left instead,
   so it loses the pull past the padding */
const BELL_BESIDE_DISC = "relative z-10 pointer-events-auto flex shrink-0 items-center gap-1.5";

/* Compact tile title: one truncated line that hugs its content so the play
   glyph sits right after the name instead of at the tile's far edge */
const COMPACT_TITLE =
  "min-w-0 truncate text-[15px] font-medium leading-snug text-white";

/* Tickets: a plain text link, no button chrome; a step quieter than the
   time beside it so the time reads first */
const TICKET_LINK =
  "relative z-10 pointer-events-auto inline-flex min-w-0 items-center gap-1 whitespace-nowrap font-mono text-xs text-neutral-400 hover:text-acid transition-colors";

function getAttractionImage(attraction: Attraction): string | null {
  if (attraction.image_url && isOwnImage(attraction.image_url)) {
    return attraction.image_url;
  }
  return null;
}

function getAttractionGenres(attraction: Attraction): string[] {
  return [...new Set((attraction.genres ?? []).filter(isDisplayableGenre))];
}

function SpotifyPlayButton({
  attraction,
  isPlaying,
  onPlay,
}: {
  attraction: Attraction;
  isPlaying: boolean;
  onPlay: (artistId: string, artistName: string) => void;
}) {
  // A bare Spotify-green glyph beside the act's name, never over a photo.
  // Hover goes white like the genre chips beside it; while playing it keeps
  // its green and picks up a soft glow, the same cue the map sidebar uses.
  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onPlay(attraction.spotify_id!, attraction.name);
      }}
      className={`relative z-10 pointer-events-auto -ml-2 shrink-0 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full transition-all hover:scale-125 ${
        isPlaying
          ? "text-[#1DB954] drop-shadow-[0_0_6px_rgba(29,185,84,0.7)]"
          : "text-[#1DB954] hover:text-white"
      }`}
      title={`Preview ${attraction.name} on Spotify`}
    >
      <PlayIcon className="h-4 w-4" />
    </button>
  );
}

export function EventCardList({
  event,
  eventGroup,
  blockedSpotifyIds,
  citySlug,
  showCity,
  prioritizeImage = false,
  initialImage,
}: EventCardListProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [showAllArtists, setShowAllArtists] = useState(false);

  const t = useTranslations("Events");
  const tCommon = useTranslations("Common");
  const tHome = useTranslations("Home");
  const locale = useLocale();
  const { isAdmin } = useAuth();
  const { play, artistId: activeArtistId, launchJukebox } = useSpotifyPlayer();
  const showCount = eventGroup ? (eventGroup.totalShowCount ?? eventGroup.events.length) : 1;
  const isGroup = eventGroup && showCount > 1;

  // Dates live in the list's day headers on every breakpoint; the card
  // itself only ever shows the time (or the shows badge for a run).
  const time = isGroup ? null : formatTime(event.local_time, locale);

  const price = isGroup
    ? (() => {
        const mins = eventGroup.events
          .map((e) => e.price_min)
          .filter((v): v is number => v != null);
        const maxes = eventGroup.events
          .map((e) => e.price_max)
          .filter((v): v is number => v != null);
        const groupMin = mins.length > 0 ? Math.min(...mins) : null;
        const groupMax = maxes.length > 0 ? Math.max(...maxes) : null;
        return formatPriceCompact(groupMin, groupMax, event.price_currency, locale, t("priceTba"));
      })()
    : formatPriceCompact(event.price_min, event.price_max, event.price_currency, locale, t("priceTba"));

  const resolvedCitySlug =
    event.venue.city_slug ?? citySlug ?? getCitySlugForVenueCity(event.venue.city ?? "");
  const eventHref = resolvedCitySlug
    ? `/${resolvedCitySlug}/event/${event.slug}`
    : event.url || "#";
  const isInternal = !!resolvedCitySlug;

  // Hero tile: event's own image, else the primary band's photo, else a plain
  // tile that still carries the primary name — so the left edge is identical
  // on every card.
  const eventOwnImage =
    event.image_url && isOwnImage(event.image_url) && !hasOnlyFallbackImages(event)
      ? event.image_url
      : null;
  // Primary pick for the tile: prefer an artist with both a photo and a
  // playable Spotify, then any with a photo, then any playable one.
  const isPlayable = (a: Attraction) => isPlayableAct(a, blockedSpotifyIds);
  const primary = getListCardPrimaryAttraction(event, blockedSpotifyIds);
  const primaryImage = primary ? getAttractionImage(primary) : null;
  const heroImage = eventOwnImage ?? primaryImage;
  const primaryHasSpotify = !!primary && isPlayable(primary);
  const primaryIsPlaying =
    primaryHasSpotify && primary!.spotify_id === activeArtistId;

  // No imagery anywhere: artists flow inline instead of stacking as rows.
  const hasAnyArtistImage = event.attractions.some(
    (a) => getAttractionImage(a) !== null
  );
  const isCompact = !heroImage && !hasAnyArtistImage;

  // Display order: featured band first, then by richness (photo beats
  // Spotify beats neither); stable within ties.
  const artistScore = (a: Attraction) =>
    (getAttractionImage(a) ? 2 : 0) + (isPlayable(a) ? 1 : 0);
  const orderedAttractions = primary
    ? [
        primary,
        ...event.attractions
          .filter((a) => a !== primary)
          .sort((x, y) => artistScore(y) - artistScore(x)),
      ]
    : [];

  // The mobile hero's play button leads with the primary when it has
  // audio, else the first act that does. It opens the page's jukebox on this
  // bill so the listener can keep swiping through the feed from here; pages
  // without a jukebox fall back to the player bar.
  const leadAct =
    primary && isPlayable(primary) ? primary : orderedAttractions.find(isPlayable) ?? null;
  const leadIsPlaying = !!leadAct && leadAct.spotify_id === activeArtistId;
  const mobileArtist = leadAct ?? primary;
  const mobileImage = eventOwnImage ?? (mobileArtist && getAttractionImage(mobileArtist)) ?? heroImage;
  const MobileImage = prioritizeImage ? "img" : DeferredListImage;
  const imageSrc = (image: string, width: number) => {
    const src = getTransformedImageUrl(image, { width });
    return initialImage?.src === src ? initialImage.dataUrl : src;
  };
  const moreMobileArtists = Math.max(0, (leadAct
    ? orderedAttractions.filter(isPlayable).length
    : orderedAttractions.length) - 1);
  // The solid Spotify-green disc: pinned over the mobile hero, and sitting in
  // the desktop card's bottom-right corner. Desktop uses the player bar;
  // mobile can open the jukebox on this bill.
  const leadPlayButton = (position: string, mode: "player" | "jukebox" = "player") => leadAct ? (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (mode === "jukebox" && launchJukebox) {
          launchJukebox({ event, artistId: leadAct.id });
        } else {
          play(leadAct.spotify_id!, leadAct.name, { event, artistId: leadAct.id });
        }
      }}
      aria-label={`${tHome("play")} ${leadAct.name}`}
      className={`${position} z-10 flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full bg-[#1DB954] text-black pointer-events-auto transition-all hover:bg-[#1ed760] active:scale-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acid ${
        leadIsPlaying
          ? "shadow-[0_0_20px_rgba(29,185,84,0.7)]"
          : "shadow-[0_4px_14px_rgba(0,0,0,0.45)]"
      }`}
    >
      <PlayIcon className="h-5 w-5" />
    </button>
  ) : null;
  const bellSlot = leadAct ? BELL_BESIDE_DISC : BELL_SLOT;

  // Middle-column header pieces: venue on the left, time / shows on the right
  const timeOrShows = isGroup ? (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setModalOpen(true);
      }}
      className="relative z-10 pointer-events-auto font-mono text-[10px] leading-none text-acid hover:text-white transition-colors"
    >
      {t("showsBadge", { count: showCount })}
    </button>
  ) : time ? (
    <span className="font-mono text-xs leading-none text-neutral-300 uppercase">
      {time}
    </span>
  ) : null;

  const ticketLink = (event.url || event.venue.url) ? (
    <a
      href={(event.url || event.venue.url) as string}
      target="_blank"
      rel="noopener noreferrer"
      data-ticket-link=""
      data-event-id={event.id}
      onClick={(e) => e.stopPropagation()}
      className={TICKET_LINK}
    >
      <span className="truncate">{price}</span>
      <ExternalLink className="h-3 w-3 shrink-0" />
    </a>
  ) : event.price_min != null ? (
    <span className="shrink-0 font-mono text-xs text-neutral-500">{price}</span>
  ) : null;

  // Compact rows (no imagery): venue on one line in the middle column, as
  // the row's own header
  const venueCluster = (
    <>
      <MapPin className="h-3.5 w-3.5 shrink-0 text-acid/50" />
      {resolvedCitySlug ? (
        <Link
          href={`/${resolvedCitySlug}/${event.venue.slug}`}
          prefetch={false}
          onClick={(e) => e.stopPropagation()}
          className="relative z-10 pointer-events-auto min-w-0 truncate text-sm text-white hover:text-acid transition-colors"
        >
          {event.venue.name}
        </Link>
      ) : (
        <span className="min-w-0 truncate text-sm text-white">
          {event.venue.name}
        </span>
      )}
      {showCity && resolvedCitySlug && CITIES[resolvedCitySlug] && (
        <span className="shrink-0 font-mono text-[10px] text-neutral-500">
          {CITIES[resolvedCitySlug].name}
        </span>
      )}
    </>
  );

  // Logistics stack, shared by the mobile overlay (left-aligned) and the
  // desktop right column (right-aligned so it shares the bell's edge):
  // venue with a pin hanging off its left (wrapping to two lines if it
  // must), then time (or the shows badge) and tickets on one quiet line,
  // then the city on cross-city pages. Left-aligned, the lines under the
  // venue are indented past the pin (14px) and its gap (6px) so they share
  // the venue text's left edge.
  const venueText = "min-w-0 text-sm leading-5 text-white line-clamp-2";
  const underVenueIndent = "pl-5";
  const logisticsStack = (align: "start" | "end") => (
    <div
      className={`flex min-w-0 flex-1 flex-col ${
        align === "end" ? "items-end text-right" : "items-start"
      }`}
    >
      <div className="flex max-w-full items-start gap-1.5">
        <MapPin className="mt-[3px] h-3.5 w-3.5 shrink-0 text-acid/50" />
        {resolvedCitySlug ? (
          <Link
            href={`/${resolvedCitySlug}/${event.venue.slug}`}
            prefetch={false}
            onClick={(e) => e.stopPropagation()}
            className={`relative z-10 pointer-events-auto hover:text-acid transition-colors ${venueText}`}
          >
            {event.venue.name}
          </Link>
        ) : (
          <span className={venueText}>{event.venue.name}</span>
        )}
      </div>
      {(timeOrShows || ticketLink) && (
        <div
          className={`mt-1 flex max-w-full items-center gap-3 ${
            align === "end" ? "justify-end" : underVenueIndent
          }`}
        >
          {timeOrShows}
          {ticketLink}
        </div>
      )}
      {showCity && resolvedCitySlug && CITIES[resolvedCitySlug] && (
        <span
          className={`mt-1 block font-mono text-[10px] text-neutral-500 ${
            align === "end" ? "" : underVenueIndent
          }`}
        >
          {CITIES[resolvedCitySlug].name}
        </span>
      )}
    </div>
  );
  const logistics = logisticsStack("end");

  const adminButton = isAdmin ? (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setAdminModalOpen(true);
      }}
      className="relative z-10 pointer-events-auto flex h-7 w-7 shrink-0 items-center justify-center rounded bg-neutral-800/60 text-neutral-500 hover:text-acid hover:bg-neutral-800 transition-all"
      title="Admin edit"
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
        <path d="m15 5 4 4" />
      </svg>
    </button>
  ) : null;

  return (
    <>
      <div
        className={`group relative overflow-hidden rounded-lg border transition-all duration-300 ${
          isCompact
            ? "border-neutral-800 bg-card lg:border-transparent lg:bg-transparent hover:border-acid/20 lg:hover:bg-white/[0.02]"
            : "border-neutral-700 bg-card hover:border-acid/30 hover:shadow-[0_0_30px_rgba(209,248,5,0.05)]"
        }`}
      >
        {/* Full-card link overlay */}
        {isInternal ? (
          <Link
            href={eventHref}
            rel={eventLinkRel(event)} prefetch={false}
            className="absolute inset-0 z-[1]"
            aria-label={`View ${event.name} details`}
          />
        ) : (
          <a
            href={eventHref}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute inset-0 z-[1]"
            aria-label={`View ${event.name} on Ticketmaster`}
          />
        )}

        {/* Layout: stacked on mobile, 3-column grid on desktop */}
        <div
          className={`lg:grid lg:grid-cols-[14rem_minmax(0,1fr)_14rem] ${
            isCompact ? "" : "lg:min-h-[10rem]"
          }`}
        >
          {/* ═══ HERO TILE (desktop only) ═══ */}
          <div
            className={`hidden lg:block lg:relative lg:overflow-hidden ${
              isCompact ? "" : "lg:border-r lg:border-neutral-800/30"
            }`}
          >
            {heroImage && (
              <>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={imageSrc(heroImage, 448)}
                  alt=""
                  loading={prioritizeImage ? "eager" : "lazy"}
                  fetchPriority={prioritizeImage ? "high" : "low"}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  style={{ objectPosition: event.image_focus || "center" }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-black/5" />
              </>
            )}
            {/* Primary name overlaid (or set on the plain tile). Full cards:
                no play button here, the primary's lineup row next door
                carries it. Compact cards: one truncated line, "+N" when
                there are more acts (linking to the event, where the full
                lineup lives), and the play glyph right after the name. */}
            <div
              className={`absolute inset-x-0 bottom-0 flex px-3 ${
                isCompact ? "inset-y-0 items-center gap-2" : "items-end py-3"
              }`}
            >
              {primary ? (
                <span className={isCompact ? COMPACT_TITLE : "min-w-0 flex-1 text-[15px] font-bold leading-snug text-white line-clamp-2"}>
                  {primary.name}
                  {isCompact && event.attractions.length > 1 && (
                    <span className="font-normal text-neutral-400">
                      {" "}+{event.attractions.length - 1}
                    </span>
                  )}
                </span>
              ) : (
                <span
                  className={`min-w-0 text-[15px] leading-snug text-white ${
                    isCompact ? "truncate font-medium" : "flex-1 line-clamp-2 font-bold"
                  }`}
                >
                  {event.name}
                </span>
              )}
              {isCompact && primary && primaryHasSpotify && (
                <SpotifyPlayButton
                  attraction={primary}
                  isPlaying={!!primaryIsPlaying}
                  onPlay={play}
                />
              )}
            </div>
          </div>

          {/* ═══ CONTENT COLUMN ═══ */}
          <div className="min-w-0 flex flex-col">
            {/* Mobile cards use the jukebox sleeve's image overlay. The
                remaining playable acts are introduced inside the jukebox. */}
            {mobileImage && (
              <div className="relative overflow-hidden lg:hidden">
                <MobileImage
                  src={imageSrc(mobileImage, 600)}
                  alt={event.name}
                  loading={prioritizeImage ? "eager" : "lazy"}
                  fetchPriority={prioritizeImage ? "high" : "low"}
                  className="block h-auto max-h-[22rem] w-full object-cover"
                  // Reserve space before loading so later lazy images stay
                  // below the fold; auto restores the photo's natural ratio.
                  style={{ aspectRatio: "auto 1 / 1", objectPosition: event.image_focus || "center" }}
                />
                <div className={`absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 via-black/60 to-transparent px-5 pb-4 pt-16 ${leadAct ? "pr-16" : ""}`}>
                  {/* Name and "+N" are flex items so the small count sits
                      centered on the name's line, a 6px gap off its last
                      letter rather than a full mono space away. */}
                  <h3 className="flex flex-wrap items-center gap-x-1.5 font-mono text-2xl font-bold uppercase leading-tight tracking-tight text-white drop-shadow-lg">
                    {mobileArtist?.name ?? event.name}
                    {moreMobileArtists > 0 && (
                      <span className="whitespace-nowrap text-base font-normal leading-none text-white/70">
                        <span aria-hidden="true">+{moreMobileArtists}</span>
                        <span className="sr-only">{t("moreArtists", { count: moreMobileArtists })}</span>
                      </span>
                    )}
                  </h3>
                  <div className="mt-2">{logisticsStack("start")}</div>
                </div>
                {leadPlayButton("absolute bottom-4 right-3", "jukebox")}
                <div className="absolute right-3 top-3 z-10 rounded-full bg-black/70 pointer-events-auto">
                  <SubscribeButton
                    type="artist"
                    targetId={event.attractions[0]?.id ?? 0}
                    targetName={event.attractions[0]?.name ?? event.name}
                    targetImage={null}
                    size="sm"
                    event={event}
                  />
                </div>
                {adminButton && <div className="absolute left-3 top-3 z-10">{adminButton}</div>}
              </div>
            )}

            {/* ── Artists ── */}
            {event.attractions.length > 0 ? (
              isCompact ? (
                /* No imagery anywhere: one strict line on desktop. Names are
                   mobile-only there (the tile carries "Primary +N"); the line
                   is venue on the left, time or the shows badge on the right. */
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-3 lg:my-auto lg:flex-nowrap lg:overflow-hidden lg:px-5 lg:py-2.5">
                  <span className="hidden lg:flex min-w-0 shrink items-center gap-1.5">
                    {venueCluster}
                  </span>
                  {orderedAttractions.map((attraction) => {
                    const hasSpotify =
                      attraction.spotify_id &&
                      !blockedSpotifyIds.includes(attraction.spotify_id);
                    const isPlaying =
                      hasSpotify && attraction.spotify_id === activeArtistId;
                    return (
                      <span
                        key={attraction.id}
                        className="flex min-w-0 items-center gap-2 lg:hidden"
                      >
                        <Link
                          href={`/artist/${attraction.slug}`}
                          rel={artistLinkRel(attraction)} prefetch={false}
                          className="relative z-10 pointer-events-auto min-w-0 truncate text-sm font-semibold text-white hover:text-acid transition-colors"
                        >
                          {attraction.name}
                        </Link>
                        {hasSpotify && (
                          <SpotifyPlayButton
                            attraction={attraction}
                            isPlaying={!!isPlaying}
                            onPlay={play}
                          />
                        )}
                      </span>
                    );
                  })}
                  {timeOrShows && (
                    <span className="hidden lg:flex lg:items-center ml-auto shrink-0 pl-3">
                      {timeOrShows}
                    </span>
                  )}
                </div>
              ) : (
                /* Desktop keeps its artist rows beside the image. */
                <div className={`hidden lg:block ${LINEUP_TOP}`}>
                  {(showAllArtists ? orderedAttractions : orderedAttractions.slice(0, LINEUP_PREVIEW)).map((attraction) => (
                    <ArtistRow
                      key={attraction.id}
                      attraction={attraction}
                      blockedSpotifyIds={blockedSpotifyIds}
                      activeArtistId={activeArtistId}
                      onPlay={play}
                      citySlug={resolvedCitySlug}
                    />
                  ))}
                  {orderedAttractions.length > LINEUP_PREVIEW && (
                    <button
                      type="button"
                      onClick={() => setShowAllArtists(!showAllArtists)}
                      className="relative z-10 pointer-events-auto w-full cursor-pointer px-4 py-2 text-left font-mono text-[11px] tracking-wider text-neutral-500 transition-colors hover:text-acid lg:px-5"
                    >
                      {showAllArtists ? (
                        tCommon("less")
                      ) : (
                        t("moreArtists", { count: orderedAttractions.length - LINEUP_PREVIEW })
                      )}
                    </button>
                  )}
                </div>
              )
            ) : (
              /* No lineup: the event name is the title (on the tile for
                 desktop, as a row on mobile); compact rows keep venue and
                 time in the middle */
              <div className={`${mobileImage ? "hidden" : "flex"} items-center px-4 py-3 lg:px-5 lg:py-2.5 ${isCompact ? "lg:my-auto lg:flex" : "lg:hidden"}`}>
                <p className="text-sm font-semibold text-white lg:hidden">{event.name}</p>
                {isCompact && (
                  <>
                    <span className="hidden lg:flex min-w-0 shrink items-center gap-1.5">
                      {venueCluster}
                    </span>
                    {timeOrShows && (
                      <span className="hidden lg:flex lg:items-center ml-auto shrink-0 pl-3">
                        {timeOrShows}
                      </span>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Cards without a photo keep their logistics below the names. */}
            {!mobileImage && <div className="flex items-center gap-3 border-t border-neutral-800/50 px-4 py-3 lg:hidden">
              {logisticsStack("start")}

              <div className="z-10 flex shrink-0 items-center gap-1 pointer-events-auto">
                {adminButton}
                <SubscribeButton
                  type="artist"
                  targetId={event.attractions[0]?.id ?? 0}
                  targetName={event.attractions[0]?.name ?? event.name}
                  targetImage={null}
                  size="sm"
                  event={event}
                />
              </div>
            </div>}
          </div>

          {/* ═══ RIGHT COLUMN (desktop only): where · when · tickets · follow ═══ */}
          {isCompact ? (
            /* Ghost row: tickets as a text link, then admin, the bell and
               the green play disc pinned to the right edge (the bell takes
               the corner on bills with no audio). The link can shrink and
               truncate; the action cluster never does, so the corner lands
               on the same line on every row, with or without tickets. The
               cluster is 44px tall with or without the disc, so bell-only
               rows match; with 6px vertical padding that is the same 56px
               the 40px bell's row was. */
            <div className={`hidden lg:flex lg:items-center lg:gap-3 lg:py-1.5 ${RIGHT_COL_X}`}>
              {ticketLink}
              <div className="ml-auto flex min-h-11 shrink-0 items-center gap-2">
                <div className={bellSlot}>
                  {adminButton}
                  <SubscribeButton
                    type="artist"
                    targetId={event.attractions[0]?.id ?? 0}
                    targetName={event.attractions[0]?.name ?? event.name}
                    targetImage={null}
                    size="sm"
                    event={event}
                  />
                </div>
                {leadPlayButton("relative")}
              </div>
            </div>
          ) : (
            <div className={`hidden lg:flex lg:flex-col ${RIGHT_COL_X} ${RIGHT_COL_TOP}`}>
              {logistics}

              {/* Actions in the bottom-right corner: admin (admins only),
                  report (on hover), bell, then the green play disc flush
                  with the column's right edge, like the mobile hero's. The
                  disc's 12px bottom padding matches the tile name's at the
                  other end of the card. Bills with no audio keep the bell in
                  the corner. */}
              <div className="mt-auto flex items-center justify-end gap-2 pb-3 pt-3">
                {adminButton}
                <div
                  className="relative z-10 pointer-events-auto opacity-0 transition-opacity group-hover:opacity-100"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                >
                  <ReportButton
                    entityType="event"
                    entityId={event.id}
                    entityName={event.name}
                  />
                </div>
                <div className={bellSlot}>
                  <SubscribeButton
                    type="artist"
                    targetId={event.attractions[0]?.id ?? 0}
                    targetName={event.attractions[0]?.name ?? event.name}
                    targetImage={null}
                    size="sm"
                    event={event}
                  />
                </div>
                {leadPlayButton("relative")}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal for grouped events */}
      {modalOpen && isGroup && (
        <EventShowingsModal
          eventGroup={eventGroup}
          onClose={() => setModalOpen(false)}
          citySlug={citySlug}
          blockedSpotifyIds={blockedSpotifyIds}
        />
      )}

      {/* Admin edit modal */}
      {adminModalOpen && (
        <EventAdminModal
          event={event}
          onClose={() => setAdminModalOpen(false)}
        />
      )}
    </>
  );
}

function ArtistRow({
  attraction,
  blockedSpotifyIds,
  activeArtistId,
  onPlay,
  citySlug,
}: {
  attraction: Attraction;
  blockedSpotifyIds: string[];
  activeArtistId: string | null;
  onPlay: (artistId: string, artistName: string) => void;
  citySlug?: string | null;
}) {
  const imageUrl = getAttractionImage(attraction);
  const genres = getAttractionGenres(attraction);
  const hasSpotify =
    attraction.spotify_id &&
    !blockedSpotifyIds.includes(attraction.spotify_id);
  const isPlaying = hasSpotify && attraction.spotify_id === activeArtistId;

  return (
    <div className="px-4 py-2 lg:px-5">
      <div className="flex items-start gap-2.5">
        {/* Tiny thumbnail — spotify-icon sized; initial when no photo */}
        <Link
          href={`/artist/${attraction.slug}`}
          rel={artistLinkRel(attraction)} prefetch={false}
          className="relative z-10 pointer-events-auto shrink-0"
        >
          <div className="relative h-7 w-7 overflow-hidden rounded-full bg-neutral-800">
            {imageUrl ? (
              <Image
                src={imageUrl}
                alt={attraction.name}
                fill
                className="object-cover"
                sizes="28px"
              />
            ) : (
              <span className="flex h-full w-full items-center justify-center font-mono text-[10px] text-neutral-600">
                {attraction.name.charAt(0).toUpperCase()}
              </span>
            )}
          </div>
        </Link>

        <div className="min-w-0 flex-1">
          {/* Name row — thumbnail height, so the two line up */}
          <div className="flex min-h-7 items-center gap-2">
            <Link
              href={`/artist/${attraction.slug}`}
              rel={artistLinkRel(attraction)} prefetch={false}
              className="relative z-10 pointer-events-auto min-w-0 text-sm font-semibold text-white hover:text-acid transition-colors line-clamp-1"
            >
              {attraction.name}
            </Link>

            {hasSpotify && (
              <SpotifyPlayButton
                attraction={attraction}
                isPlaying={!!isPlaying}
                onPlay={onPlay}
              />
            )}

            {attraction.external_links && (
              <div className="relative z-10 pointer-events-auto">
                <ArtistExternalLinks
                  links={attraction.external_links}
                  size="sm"
                />
              </div>
            )}
          </div>

          {/* Genres sit under the name, flush with its left edge, a quiet
              dot between each pair; each one filters the feed. No top
              margin: the name row's centering slack already leaves the same
              4px the right column puts between the venue and the time line. */}
          {genres.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
              {genres.map((genre, i) => (
                <Fragment key={genre}>
                  {i > 0 && (
                    <span aria-hidden="true" className="font-mono text-[10px] text-neutral-400">
                      ·
                    </span>
                  )}
                  <GenreChip genre={genre} citySlug={citySlug} />
                </Fragment>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
